import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";

// INR deposit gateways. `key` matches the frontend upiMethod + the backend
// status.json / API keys exactly.
const GATEWAYS: { key: string; label: string }[] = [
  { key: "paytm", label: "Paytm" },
  { key: "okpay", label: "OkPay" },
  { key: "upi_p2p", label: "UPIp2p" },
  { key: "phonepe", label: "PhonePe" },
  { key: "upi_fast_v", label: "UPIfastv" },
  { key: "imps", label: "Imps" },
];

type StatusResponse = { success: boolean; gateways: Record<string, boolean> };

const Toggle = ({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}) => (
  <label className={`relative inline-flex items-center ${disabled ? "opacity-60 cursor-wait" : "cursor-pointer"}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="sr-only peer"
    />
    <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-600 transition-colors">
      <div className="w-5 h-5 bg-white rounded-full shadow transform transition-transform peer-checked:translate-x-5" />
    </div>
  </label>
);

export default function PaymentGateways() {
  const [gateways, setGateways] = useState<Record<string, boolean> | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    apiFetch<StatusResponse>("/api/gateway-status")
      .then((r) => setGateways(r.gateways || {}))
      .catch(() => setErr("Failed to load gateway status"));

  useEffect(() => {
    load();
  }, []);

  const toggle = async (key: string) => {
    if (!gateways || savingKey) return;
    const next = !(gateways[key] !== false); // currently shown unless explicitly false
    // optimistic update
    setGateways({ ...gateways, [key]: next });
    setSavingKey(key);
    setErr(null);
    try {
      const r = await apiFetch<StatusResponse>("/api/gateway-status/toggle", {
        method: "POST",
        body: JSON.stringify({ gateway: key, enabled: next }),
      });
      if (r.gateways) setGateways(r.gateways);
    } catch (e) {
      // revert on failure
      setGateways((prev) => (prev ? { ...prev, [key]: !next } : prev));
      setErr("Failed to update. Please try again.");
    } finally {
      setSavingKey(null);
    }
  };

  if (!gateways)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600 text-lg animate-pulse">Loading...</div>
      </div>
    );

  return (
    <div className="ml-0 lg:ml-[256px]">
      <div className="max-w-4xl mx-auto p-6 space-y-6 bg-gray-50 min-h-screen">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Gateways</h1>
          <p className="text-gray-500 mt-1">
            Turn an INR deposit gateway off while it's under maintenance — it hides
            instantly on the deposit popup and page for all users. Turn it back on
            when the provider is working again.
          </p>
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">
            {err}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md divide-y">
          {GATEWAYS.map(({ key, label }) => {
            const on = gateways[key] !== false;
            return (
              <div key={key} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-medium text-gray-800">{label}</span>
                  <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      on
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {on ? "Live" : "Under maintenance"}
                  </span>
                </div>
                <Toggle
                  checked={on}
                  disabled={savingKey === key}
                  onChange={() => toggle(key)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
